import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, LOGO_URL } from "../lib/api";
import { ShieldCheck, Eye, EyeOff, CheckCircle, AlertCircle } from "lucide-react";

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Les mots de passe ne correspondent pas.");
      return;
    }
    if (password.length < 8) {
      setError("Le mot de passe doit contenir au moins 8 caractères.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setSuccess(true);
      setTimeout(() => navigate("/admin-login"), 3000);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Erreur lors de la réinitialisation. Le lien est peut-être expiré.");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-[calc(100vh-200px)] flex items-center justify-center px-4 py-10">
        <div className="bg-white border border-neutral-200 rounded-2xl p-8 max-w-md w-full text-center">
          <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
          <h1 className="font-heading font-black text-2xl mb-2">Lien invalide</h1>
          <p className="text-neutral-500 text-sm mb-6">Ce lien de réinitialisation est invalide ou a expiré.</p>
          <Link to="/forgot-password" className="imora-btn-primary inline-flex">
            Faire une nouvelle demande
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-200px)] flex items-center justify-center px-4 py-10">
      <div className="bg-white border border-neutral-200 rounded-2xl p-8 max-w-md w-full">
        <div className="text-center mb-6">
          <img src={LOGO_URL} alt="IMORA" className="h-14 w-14 mx-auto rounded-xl mb-3" />
          <div className="inline-flex items-center gap-2 bg-neutral-900 text-white text-xs font-bold px-3 py-1 rounded-full mb-2">
            <ShieldCheck size={14} /> NOUVEAU MOT DE PASSE
          </div>
          <h1 className="font-heading font-black text-2xl tracking-tighter">Choisir un nouveau mot de passe</h1>
          <p className="text-sm text-neutral-500 mt-1">8 caractères minimum avec lettres et chiffres</p>
        </div>

        {success ? (
          <div className="text-center py-6">
            <div className="bg-green-50 border border-green-200 rounded-xl p-6 mb-4">
              <CheckCircle className="h-10 w-10 text-green-500 mx-auto mb-3" />
              <p className="font-heading font-bold text-green-800 text-lg">Mot de passe réinitialisé !</p>
              <p className="text-sm text-green-700 mt-1">
                Votre nouveau mot de passe est actif. Redirection vers la connexion…
              </p>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs font-bold text-neutral-600 uppercase">Nouveau mot de passe</label>
              <div className="relative mt-1">
                <input
                  type={showPw ? "text" : "password"}
                  required
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="new-password-input"
                  placeholder="Min 8 car. (lettres + chiffres)"
                  className="w-full h-12 px-4 pr-12 border-2 border-neutral-200 rounded-lg focus:border-[#FF6B1A] focus:outline-none text-sm"
                />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400">
                  {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div>
              <label className="text-xs font-bold text-neutral-600 uppercase">Confirmer le mot de passe</label>
              <input
                type={showPw ? "text" : "password"}
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                data-testid="confirm-password-input"
                placeholder="Répétez le mot de passe"
                className="mt-1 w-full h-12 px-4 border-2 border-neutral-200 rounded-lg focus:border-[#FF6B1A] focus:outline-none text-sm"
              />
            </div>

            {error && (
              <p data-testid="reset-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="reset-submit-btn"
              className="w-full h-12 bg-neutral-900 hover:bg-black text-white font-bold rounded-lg transition disabled:opacity-60"
            >
              {loading ? "Réinitialisation…" : "Confirmer le nouveau mot de passe"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;
